// HT/FT — bars view (Phase 3). W/D/L split at HT and FT as two stacked bars.
//
// The `.htft-bf-*` fills have CSS `width: 0%` and no transition, so — like
// OverUnder/StatPairBars — React owns the shell (labels + stat text) and D3
// animates the fill widths in a useEffect. The consumer keys this component by
// fixture+team (see HtFt), so those changes remount it (fills reset to 0%, then
// animate up, matching the legacy cacheKey rebuild); a timeline change keeps the
// mount and animates current→new. Same DOM as the legacy renderer.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import type { HtftCountData, HtftCounts } from "@bento/shared";

const DURATION = 300;

export interface HtFtBarsProps {
  counts: HtftCountData;
  total: number;
}

export default function HtFtBars({ counts, total }: HtFtBarsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pct = (n: number) => Math.round((n / total) * 100);

  const rows: { label: string; c: HtftCounts }[] = [
    { label: "Half Time", c: counts.htC },
    { label: "Full Time", c: counts.ftC },
  ];
  const sig = JSON.stringify(rows.map((r) => r.c)) + `|${total}`;

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    wrap.querySelectorAll<HTMLElement>(".ou-row").forEach((row, i) => {
      const c = rows[i]!.c;
      const set = (sel: string, v: number) =>
        select(row.querySelector<HTMLElement>(sel)!)
          .transition()
          .duration(DURATION)
          .ease(easeCubicInOut)
          .style("width", pct(v) + "%");
      set(".htft-bf-w", c.W);
      set(".htft-bf-d", c.D);
      set(".htft-bf-l", c.L);
    });
    // `sig` covers both rows' counts + total — the complete dependency.
  }, [sig]);

  return (
    <div className="data-wrapper top-align" ref={wrapRef}>
      <div className="htft-bars-block">
        {rows.map((r) => (
          <div className="ou-row" key={r.label}>
            <div className="sc-stat-header">
              <span className="sc-stat-name">{r.label}</span>
              <span className="htft-bstat">
                {/* One text node per span (`W 40%`), matching the legacy
                    innerHTML — splitting it (`W {n}%`) makes 3 text nodes whose
                    shaping differs by a sub-pixel and fails the A/B. */}
                <span className="htft-stat-w">{`W ${pct(r.c.W)}%`}</span>
                <span className="htft-stat-d">{`D ${pct(r.c.D)}%`}</span>
                <span className="htft-stat-l">{`L ${pct(r.c.L)}%`}</span>
              </span>
            </div>
            <div className="ou-bar-bg">
              <div className="htft-bf-w bento-fill" />
              <div className="htft-bf-d bento-fill" />
              <div className="htft-bf-l bento-fill" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
